/* FREDDY Pantroom - catalog filtering (collection + search pages). 00-architecture §E.6, 03-commerce §2.6.
   Concept shop.js catalog behaviour mapped onto Shopify storefront filtering + the Section Rendering API:
   checkbox facets with counts, price "up to" slider, sort, active chips, client "search within selection",
   mobile toggle/apply. Reads <script type="application/json" data-freddy-catalog> printed after section.shop-shell. */
(() => {
  'use strict';

  const FREDDY = (window.FREDDY = window.FREDDY || {});
  const instances = new WeakMap();
  const ENTITIES = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&#x27;': "'" };

  const lang = () => (document.documentElement.lang || (FREDDY.settings && FREDDY.settings.lang) || 'nl').toLowerCase();
  const decode = (s) => String(s == null ? '' : s).replace(/&(?:amp|lt|gt|quot|#39|#x27);/g, (m) => ENTITIES[m]);
  const isOne = (n) => n === 1 || (n === 0 && lang().indexOf('fr') === 0);

  function money(cents) {
    if (typeof FREDDY.formatMoney === 'function') return FREDDY.formatMoney(cents);
    const value = (Math.round(cents) / 100).toFixed(2).replace('.', ',');
    return '€ ' + value;
  }

  function readConfig(shell) {
    const wrapper = shell.closest('.shopify-section') || shell.parentElement;
    const script = wrapper ? wrapper.querySelector('script[data-freddy-catalog]') : null;
    if (!script) return null;
    try {
      return { script, data: JSON.parse(script.textContent) };
    } catch (e) {
      return null;
    }
  }

  function init(shell) {
    if (!shell || instances.has(shell)) return;
    const found = readConfig(shell);
    if (!found) return;
    const cfg = found.data;
    const q = (sel) => shell.querySelector(sel);
    const el = {
      script: found.script,
      grid: q('#product-grid'),
      active: q('#active-filters'),
      count: q('#result-count'),
      empty: q('#empty-results'),
      sort: q('#sort'),
      range: q('#price-max'),
      output: q('#price-output'),
      search: q('#catalog-search'),
      toggle: q('#toggle-filters'),
      apply: q('#apply-filters'),
      filters: q('#filters')
    };
    if (!el.grid) return;
    const state = { cfg, el, shell, controller: null, timer: 0, cards: [], serverCount: '', serverEmptyHidden: true, lastQuery: location.search.replace(/^\?/, '') };
    instances.set(shell, state);

    cacheServerState(state);
    restoreClientQuery(state, new URLSearchParams(location.search));
    applyClient(state);

    shell.addEventListener('change', (event) => onChange(state, event));
    shell.addEventListener('input', (event) => onInput(state, event));
    shell.addEventListener('click', (event) => onClick(state, event));
  }

  function boxes(state) {
    return state.el.filters ? [...state.el.filters.querySelectorAll('.filter-options input[type=checkbox]')] : [];
  }

  function buildParams(state) {
    const { cfg, el } = state;
    const params = new URLSearchParams();
    const base = cfg.base_params || {};
    Object.keys(base).forEach((k) => {
      if (base[k] !== '' && base[k] != null) params.set(k, base[k]);
    });
    boxes(state)
      .filter((x) => x.checked)
      .forEach((x) => params.append((cfg.params && cfg.params[x.name]) || x.name, x.value));
    if (el.range && cfg.price_param) {
      const max = Number(el.range.value);
      if (max < Number(cfg.price_max)) params.set(cfg.price_param, String(max));
    }
    if (el.sort && cfg.sort_map) {
      const sortBy = cfg.sort_map[el.sort.value];
      if (sortBy) params.set('sort_by', sortBy);
    }
    if (cfg.client_q_param && el.search) {
      const term = el.search.value.trim();
      if (term) params.set(cfg.client_q_param, term);
    }
    return params;
  }

  function writeUrl(state, params) {
    const query = params.toString();
    state.lastQuery = query;
    try {
      history.replaceState(history.state, '', location.pathname + (query ? '?' + query : '') + location.hash);
    } catch (e) {
      /* ignore (sandboxed editor preview) */
    }
  }

  function countText(state, n) {
    const strings = state.cfg.strings || {};
    const tpl = isOne(n) ? strings.one : strings.other;
    return decode(tpl || '[count]').replace('[count]', String(n));
  }

  function cacheServerState(state) {
    const { el } = state;
    state.cards = [...el.grid.children];
    state.serverCount = el.count ? el.count.textContent : '';
    state.serverEmptyHidden = el.empty ? el.empty.hidden : true;
  }

  function restoreClientQuery(state, params) {
    const { cfg, el } = state;
    if (!el.search || !cfg.client_q_param) return;
    el.search.value = params.get(cfg.client_q_param) || '';
  }

  function cardName(card) {
    const h = card.querySelector('h3');
    return h ? h.textContent.trim() : '';
  }

  /* Concept matches() text part + name sort, on the cards of the current page (no fetch). */
  function applyClient(state) {
    const { cfg, el } = state;
    const term = el.search ? el.search.value.trim().toLowerCase() : '';
    const words = term ? term.split(/\s+/) : [];
    let list = state.cards;
    if (words.length) {
      const texts = new Map((cfg.items || []).map((item) => [String(item.id), String(item.text || '').toLowerCase()]));
      list = list.filter((card) => {
        const text = texts.get(card.getAttribute('data-product')) || card.textContent.toLowerCase();
        return words.every((w) => text.includes(w));
      });
    }
    if (el.sort && el.sort.value === 'name' && cfg.name_sort_client && cfg.single_page) {
      const locale = lang();
      list = list.slice().sort((a, b) => cardName(a).localeCompare(cardName(b), locale));
    }
    const current = [...el.grid.children];
    const same = current.length === list.length && current.every((node, i) => node === list[i]);
    if (!same) el.grid.replaceChildren(...list);
    if (words.length) {
      if (el.count) el.count.textContent = countText(state, list.length);
      if (el.empty) el.empty.hidden = list.length !== 0;
    } else {
      if (el.count) el.count.textContent = state.serverCount;
      if (el.empty) el.empty.hidden = state.serverEmptyHidden;
    }
  }

  function swapAfter(state, doc, selector, anchor) {
    const column = state.el.grid.parentElement;
    if (!column) return;
    const current = [...column.children].find((node) => node.matches(selector));
    const next = doc.querySelector('.shop-shell ' + selector);
    if (next) {
      const imported = document.importNode(next, true);
      if (current) current.replaceWith(imported);
      else if (anchor) anchor.after(imported);
      else column.append(imported);
    } else if (current) {
      current.remove();
    }
  }

  /* ---- facet lists: aside#filters itself is never replaced (focus, scroll, details[open]) ----
     The server decides which values a facet lists (unfiltered: in-stock sizes only and no empty values;
     filtered: every Search & Discovery value), so a response can list other values than the page shows.
     Same values -> counts + checked state in place; other values -> that facet's .filter-options is rebuilt
     (focus kept on the same checkbox); a facet the server no longer prints is removed, a new one inserted. */
  function facetKey(details) {
    const box = details.querySelector('.filter-options input[type=checkbox]');
    if (box) return 'list:' + box.name;
    return details.querySelector('#price-max') ? 'price' : null;
  }

  const optionKeys = (options) => [...options.querySelectorAll('small[data-count]')].map((small) => small.getAttribute('data-count')).join('\n');

  function syncOptions(current, fresh) {
    if (!current || !fresh) return;
    if (optionKeys(current) !== optionKeys(fresh)) {
      const active = document.activeElement;
      const focused = active && current.contains(active) && active.matches('input[type=checkbox]') ? [active.name, active.value] : null;
      current.innerHTML = fresh.innerHTML;
      if (focused) {
        const again = [...current.querySelectorAll('input[type=checkbox]')].find((x) => x.name === focused[0] && x.value === focused[1]);
        if (again) again.focus({ preventScroll: true });
      }
      return;
    }
    const counts = new Map();
    fresh.querySelectorAll('small[data-count]').forEach((small) => counts.set(small.getAttribute('data-count'), small));
    current.querySelectorAll('small[data-count]').forEach((small) => {
      const freshSmall = counts.get(small.getAttribute('data-count'));
      small.textContent = freshSmall ? freshSmall.textContent : '0';
      const label = small.closest('label');
      const box = label ? label.querySelector('input[type=checkbox]') : null;
      const freshLabel = freshSmall ? freshSmall.closest('label') : null;
      const freshBox = freshLabel ? freshLabel.querySelector('input[type=checkbox]') : null;
      if (box) box.checked = !!(freshBox && freshBox.checked);
    });
  }

  function syncFacets(state, next) {
    const filters = state.el.filters;
    const freshFilters = next.querySelector('#filters');
    if (!filters || !freshFilters) return;
    const current = new Map();
    filters.querySelectorAll('details').forEach((details) => {
      const key = facetKey(details);
      if (key) current.set(key, details);
    });
    const fresh = [...freshFilters.querySelectorAll('details')].map((details) => [facetKey(details), details]).filter(([key]) => key);
    const freshKeys = new Set(fresh.map(([key]) => key));
    let anchor = null;
    fresh.forEach(([key, freshDetails]) => {
      let details = current.get(key);
      if (key === 'price') {
        if (details) anchor = details;
        return;
      }
      if (details) {
        syncOptions(details.querySelector('.filter-options'), freshDetails.querySelector('.filter-options'));
      } else {
        details = document.importNode(freshDetails, true);
        const ref = anchor ? anchor.nextElementSibling : filters.querySelector('details') || filters.querySelector('#apply-filters');
        if (ref) ref.before(details);
        else filters.append(details);
      }
      anchor = details;
    });
    current.forEach((details, key) => {
      if (key !== 'price' && !freshKeys.has(key)) details.remove();
    });
  }

  function render(state, html, fromHistory) {
    const { el } = state;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('.shop-shell');
    if (!next) throw new Error('freddy-facets: no .shop-shell in response');
    const pick = (sel) => next.querySelector(sel);

    const grid = pick('#product-grid');
    if (grid) el.grid.innerHTML = grid.innerHTML;
    if (el.active) {
      const active = pick('#active-filters');
      el.active.innerHTML = active ? active.innerHTML : '';
    }
    const count = pick('#result-count');
    if (el.count && count) el.count.textContent = count.textContent;
    const empty = pick('#empty-results');
    if (el.empty && empty) el.empty.hidden = empty.hidden;

    syncFacets(state, next);

    if (el.sort) {
      const selected = next.querySelector('#sort option[selected]');
      el.sort.value = selected ? selected.value : 'featured';
    }
    if (fromHistory && el.range) {
      const range = pick('#price-max');
      if (range) {
        el.range.value = range.getAttribute('value');
        if (el.output) el.output.textContent = money(Number(el.range.value) * 100);
      }
    }

    swapAfter(state, doc, '.freddy-pagination', el.grid);
    swapAfter(state, doc, '.freddy-search-other', null);

    const script = doc.querySelector('script[data-freddy-catalog]');
    if (script) {
      try {
        Object.assign(state.cfg, JSON.parse(script.textContent));
        if (el.script) el.script.textContent = script.textContent;
      } catch (e) {
        /* keep the previous config */
      }
    }

    cacheServerState(state);
    applyClient(state);
  }

  function load(state, params, fromHistory) {
    const { cfg } = state;
    const query = params.toString();
    const request = new URLSearchParams(params);
    request.set('section_id', cfg.section_id);
    const url = cfg.url + (cfg.url.indexOf('?') === -1 ? '?' : '&') + request.toString();

    if (state.controller) state.controller.abort();
    const controller = new AbortController();
    state.controller = controller;
    state.shell.setAttribute('aria-busy', 'true');

    return fetch(url, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then((html) => {
        if (controller !== state.controller) return;
        render(state, html, fromHistory);
        if (!fromHistory) writeUrl(state, params);
        else state.lastQuery = query;
        document.dispatchEvent(new CustomEvent('freddy:facets:updated', { detail: { url: location.href } }));
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        /* Fallback: full page load of the same state. */
        location.assign(cfg.url + (query ? (cfg.url.indexOf('?') === -1 ? '?' : '&') + query : ''));
      })
      .finally(() => {
        if (controller === state.controller) {
          state.controller = null;
          state.shell.removeAttribute('aria-busy');
        }
      });
  }

  function schedule(state, delay) {
    clearTimeout(state.timer);
    /* A response for an older state must not render once the shopper has changed the filters again:
       it would reset the checkboxes before the new state is read in buildParams(). */
    if (state.controller) {
      state.controller.abort();
      state.controller = null;
    }
    state.timer = setTimeout(() => load(state, buildParams(state), false), delay);
  }

  function onChange(state, event) {
    const t = event.target;
    if (t === state.el.sort) {
      schedule(state, 0);
    } else if (t.matches && t.matches('#filters .filter-options input[type=checkbox]')) {
      schedule(state, 0);
    }
  }

  function onInput(state, event) {
    const { el } = state;
    const t = event.target;
    if (t === el.range) {
      if (el.output) el.output.textContent = money(Number(el.range.value) * 100);
      schedule(state, 250);
    } else if (t === el.search) {
      applyClient(state);
      writeUrl(state, buildParams(state));
    }
  }

  function reset(state) {
    const { cfg, el } = state;
    boxes(state).forEach((x) => {
      x.checked = false;
    });
    if (el.search) el.search.value = '';
    if (el.range) {
      el.range.value = String(cfg.price_max);
      if (el.output) el.output.textContent = money(Number(el.range.value) * 100);
    }
    if (el.sort) el.sort.value = 'featured';
    clearTimeout(state.timer);
    load(state, buildParams(state), false);
  }

  function onClick(state, event) {
    const { el } = state;
    const t = event.target;
    if (!t.closest) return;
    const chip = t.closest('.filter-chip');
    if (chip && el.active && el.active.contains(chip)) {
      const chips = [...el.active.querySelectorAll('.filter-chip')];
      const checked = boxes(state).filter((x) => x.checked);
      const box = checked[chips.indexOf(chip)];
      if (box) {
        box.checked = false;
        schedule(state, 0);
      }
      return;
    }
    if (t.closest('.filter-reset')) {
      reset(state);
      return;
    }
    if (el.toggle && t.closest('#toggle-filters') === el.toggle) {
      const open = !state.shell.classList.contains('filters-open');
      state.shell.classList.toggle('filters-open', open);
      el.toggle.setAttribute('aria-expanded', String(open));
      return;
    }
    if (el.apply && t.closest('#apply-filters') === el.apply) {
      state.shell.classList.remove('filters-open');
      if (el.toggle) el.toggle.setAttribute('aria-expanded', 'false');
      if (el.count) el.count.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  function initAll(root) {
    (root || document).querySelectorAll('.shop-shell').forEach(init);
  }

  window.addEventListener('popstate', () => {
    document.querySelectorAll('.shop-shell').forEach((node) => {
      const state = instances.get(node);
      if (!state) return;
      const params = new URLSearchParams(location.search);
      params.delete('section_id');
      if (params.toString() === state.lastQuery) return;
      restoreClientQuery(state, params);
      load(state, params, true);
    });
  });

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  FREDDY.facets = Object.assign(FREDDY.facets || {}, {
    init: initAll,
    refresh(node) {
      const state = instances.get(node || document.querySelector('.shop-shell'));
      if (state) load(state, buildParams(state), false);
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
})();
